import authStorage from "src/utils/authStorage";
import ActionCable from 'actioncable';
import { wsURL } from '../../../utils/api';

/**
 * Core WebSocket functionality
 * Handles basic connection management and initialization
 */
class WebSocketCore {
  constructor() {
    this.cable = null;
    this.initialized = false;

    // Use REACT_APP_WEBSOCKET_URL if available, otherwise fallback to wsURL function
    if (process.env.REACT_APP_WEBSOCKET_URL) {
      this.baseUrl = process.env.REACT_APP_WEBSOCKET_URL;
    } else {
      // Fallback to wsURL function from api.js
      this.baseUrl = wsURL();
    }

    // Ensure we're using wss:// for HTTPS pages
    if (window.location.protocol === 'https:' && this.baseUrl.startsWith('ws://')) {
      console.warn('WebSocket URL is using ws:// on HTTPS page, converting to wss://');
      this.baseUrl = this.baseUrl.replace(/^ws:/, 'wss:');
    }

    console.log('WebSocket URL:', this.baseUrl);
    console.log('Current protocol:', window.location.protocol);
    console.log('WebSocket protocol:', this.baseUrl.startsWith('wss:') ? 'wss://' : 'ws://');
    
    this.connectionAttempts = 0;
    this.maxConnectionAttempts = 5;
    this._initializing = false; // Flag to prevent multiple simultaneous initialization attempts
  }

  /**
   * Initialize the WebSocket connection
   * @param {boolean} forceReconnect - Whether to force a reconnection if already connected
   */
  initialize(forceReconnect = false) {
    // Prevent multiple simultaneous initialization attempts
    if (this._initializing) {
      console.log('WebSocket initialization already in progress, skipping');
      return;
    }
    
    this._initializing = true;
    
    if (this.initialized && !forceReconnect) {
      this._initializing = false;
      return;
    }
    
    // Clean up existing connection if we're forcing a reconnect
    if (this.cable && forceReconnect) {
      try {
        this.cable.disconnect();
        console.log('WebSocket disconnected for reconnection');
      } catch (e) {
        console.warn('Error disconnecting WebSocket:', e);
      }
      
      // Add a small delay after disconnecting
      setTimeout(() => {
        this._completeInitialization(forceReconnect);
      }, 500);
    } else {
      this._completeInitialization(forceReconnect);
    }
  }
  
  /**
   * Complete the initialization process
   * @private
   * @param {boolean} forceReconnect - Whether this is a forced reconnection
   */
  _completeInitialization(forceReconnect) {
    // Reset connection attempts if this is a forced reconnect
    if (forceReconnect) {
      this.connectionAttempts = 0;
    }
    
    // First check if the WebSocket endpoint is actually available
    this._checkWebSocketEndpoint()
      .then(available => {
        if (available) {
          try {
            console.log(`WEBSOCKET INFO: Connecting to: ${this.baseUrl} (attempt ${this.connectionAttempts + 1})`);
            
            // Add error handling for WebSocket connection
            try {
              // Get token from localStorage for authentication
              const token = authStorage.getItem('@sacgpt:token');

              // Use a more robust way to create the ActionCable consumer with authentication
              if (token) {
                console.log('WEBSOCKET INFO: Creating ActionCable consumer with token');
                // Add token to the URL as a query parameter for authentication
                const urlWithAuth = `${this.baseUrl}?token=${encodeURIComponent(token)}`;
                this.cable = ActionCable.createConsumer(urlWithAuth);
              } else {
                console.log('WEBSOCKET INFO: Creating ActionCable consumer without token');
                this.cable = ActionCable.createConsumer(this.baseUrl);
              }

              console.log('WEBSOCKET INFO: Cable created:', {
                cable: this.cable,
                subscriptions: this.cable?.subscriptions,
                connection: this.cable?.connection,
                consumer: this.cable?.consumer
              });
              
              // TEMPORARILY DISABLED: Event listeners may be causing reconnection issues
              console.log('WEBSOCKET INFO: WebSocket event listeners temporarily disabled for debugging');
              /*
              // Add comprehensive event listeners for WebSocket connection
              if (this.cable && this.cable.connection) {
                // Monitor the connection object
                this.cable.connection.monitor.visibilityDidChange = this.cable.connection.monitor.visibilityDidChange.bind(this.cable.connection.monitor);

                // Add more robust error handling
                if (this.cable.connection.webSocket) {
                  // WebSocket error event
                  this.cable.connection.webSocket.addEventListener('error', (event) => {
                    console.error('WEBSOCKET ERROR: Connection error event:', event);
                    this._handleInitializationError();
                  });

                  // WebSocket close event
                  this.cable.connection.webSocket.addEventListener('close', (event) => {
                    console.warn(`WEBSOCKET WARN: Connection closed (code: ${event.code}, reason: ${event.reason || 'none'})`);

                    // Only handle as error if not a normal closure
                    if (event.code !== 1000) {
                      this._handleInitializationError();
                    }
                  });
                }
              }
              */
            } catch (wsError) {
              console.error('WEBSOCKET ERROR: Failed to create ActionCable consumer:', wsError);
              this._handleInitializationError();
              return;
            }
            
            this.initialized = true;
            this._initializing = false;
            
            console.log('WEBSOCKET INFO: Service initialized successfully');
          } catch (e) {
            console.error('WEBSOCKET ERROR: Failed to initialize WebSocket:', e);
            this._handleInitializationError();
          }
        } else {
          console.error('WebSocket endpoint is not available');
          this._handleInitializationError();
        }
      })
      .catch(error => {
        console.error('Error checking WebSocket endpoint:', error);
        this._handleInitializationError();
      });
  }
  
  /**
   * Check if the WebSocket endpoint is available
   * @private
   * @returns {Promise<boolean>} - Whether the endpoint is available
   */
  _checkWebSocketEndpoint() {
    return new Promise((resolve) => {
      // Use a timeout to prevent hanging
      const timeout = setTimeout(() => {
        console.warn('WebSocket endpoint check timed out');
        resolve(true); // Resolve as true anyway to attempt connection
      }, 3000);
      
      // Try to make a simple HTTP request to the server
      // We'll use the HTTP equivalent of the WebSocket URL
      const httpUrl = this.baseUrl.replace(/^ws/, 'http').replace(/^wss/, 'https');
      
      console.log('Checking WebSocket endpoint availability:', httpUrl);
      
      // Try a simpler approach - just check if the server is reachable at all
      const baseUrl = httpUrl.substring(0, httpUrl.indexOf('/cable'));
      
      // Try both the ping endpoint and a simple request to the root
      fetch(baseUrl + '/api/v1/widget/ping')
        .then(response => {
          clearTimeout(timeout);
          console.log('WebSocket endpoint check successful:', response.status);
          resolve(true);
        })
        .catch((error) => {
          console.warn('WebSocket endpoint check failed:', error);
          clearTimeout(timeout);
          
          // Even if the check fails, we'll still try to connect
          // This is because the WebSocket server might be running even if the ping endpoint is not available
          resolve(true);
        });
    });
  }
  
  /**
   * Handle initialization errors
   * @private
   */
  _handleInitializationError() {
    // Retry with exponential backoff
    if (this.connectionAttempts < this.maxConnectionAttempts) {
      const backoffTime = Math.min(1000 * Math.pow(2, this.connectionAttempts), 10000);
      this.connectionAttempts++;
      
      console.log(`WEBSOCKET INFO: Retrying WebSocket connection in ${backoffTime}ms... (attempt ${this.connectionAttempts} of ${this.maxConnectionAttempts})`);
      setTimeout(() => {
        this._initializing = false;
        
        // Try with an alternative URL if we've had multiple failures on localhost
        // Only do this in development mode, not in production
        if (window.location.hostname === 'localhost' && process.env.NODE_ENV !== 'production') {
          // Try different ports if we're on localhost
          const alternativePorts = [3000, 3001, 5000, 3030, 8080];
          let currentPort = 0;
          
          try {
            // More robust port extraction
            const portMatch = this.baseUrl.match(/:(\d+)/);
            if (portMatch && portMatch[1]) {
              currentPort = parseInt(portMatch[1], 10);
            }
          } catch (error) {
            console.warn('WEBSOCKET WARN: Error parsing port number', error);
          }
          
          // Find the next port to try with better handling
          if (this.connectionAttempts >= 2) {
            let nextPortIndex = alternativePorts.indexOf(currentPort) + 1;
            
            // If current port wasn't found or we're at the end, start from beginning
            if (nextPortIndex <= 0 || nextPortIndex >= alternativePorts.length) {
              nextPortIndex = 0;
            }
            
            const nextPort = alternativePorts[nextPortIndex];
            
            // Only change if we have a valid port
            if (nextPort) {
              this.baseUrl = `ws://localhost:${nextPort}/cable`;
              console.log(`WEBSOCKET INFO: Trying alternative WebSocket URL: ${this.baseUrl}`);
            }
          }
        }
        
        // Try to initialize again
        this.initialize(true);
      }, backoffTime);
    } else {
      console.error(`WEBSOCKET ERROR: Failed to connect after ${this.maxConnectionAttempts} attempts`);
      this._initializing = false;
      
      // Display a more helpful error message in the console
      console.error(`
        WEBSOCKET CONNECTION FAILED. Please check:
        1. Is the Rails server running?
        2. Is ActionCable properly configured?
        3. Is the correct port being used? (current: ${this.baseUrl})
        4. Are there any CORS issues?
        5. Is the server reachable from this client?
        
        Environment: ${process.env.NODE_ENV || 'development'}
        
        For production environments (like Heroku), ensure these environment variables are set:
        - REACT_APP_API_URL: The base URL of your API
        - REACT_APP_WEBSOCKET_URL: The WebSocket URL (wss://your-app.herokuapp.com/cable)
        
        The system will retry connecting when needed.
      `);
      
      // Dispatch an event that the app can listen for
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('websocket_connection_failed', {
          detail: { attempts: this.connectionAttempts, baseUrl: this.baseUrl }
        }));
      }
    }
  }
  
  /**
   * Get the ActionCable consumer (alias for cable)
   * @returns {Object|null} The ActionCable consumer or null if not connected
   */
  get consumer() {
    return this.cable;
  }
  
  /**
   * Check if WebSocket is connected
   * @returns {boolean} True if connected
   */
  isConnected() {
    return this.initialized && this.cable !== null;
  }
}

export default WebSocketCore;
